// src/pages/payments/RecordPayment.tsx
// Complete with receipt security - WITH INSTALLMENTS SUPPORT

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '../../config/supabase/client';
import { useAuth } from '../../hooks/useAuth';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import {
  ArrowLeft,
  CreditCard,
  User,
  DollarSign,
  Building,
  Banknote,
  Smartphone,
  Wallet,
  Loader2,
  CheckCircle,
  Info,
  X,
  Save,
  Upload,
  Image,
  File,
  Eye,
  AlertTriangle,
  Calendar,
  Clock,
  Shield,
  Receipt,
  Printer,
  Download,
  QrCode,
  Barcode,
  Key,
  Copy,
  Check,
  Sparkles,
  Gift,
  Tag,
  ExternalLink,
  Verified,
  Lock,
  ShieldCheck,
  ShieldAlert,
} from 'lucide-react';
import JsBarcode from 'jsbarcode';
import { QRCodeCanvas } from 'qrcode.react';
import transferSuccessImg from '../../assets/transfer.png';

// Types
interface Student {
  id: string;
  first_name: string;
  last_name: string;
  admission_number: string;
  class_name?: string;
  branch_id?: string;
  email?: string;
  phone?: string;
}

interface FeeAssignment {
  id: string;
  fee_id: string;
  fee_name: string;
  fee_category: string;
  fee_description: string;
  original_amount: number;
  discount_amount: number;
  amount_due: number;
  amount_paid: number;
  balance: number;
  payment_status: string;
  due_date: string;
  is_active: boolean;
  session_id?: string;
  term_id?: string;
  session_name?: string;
  term_name?: string;
  academic_session?: string;
  academic_term?: string;
  student_id?: string;
}

interface UploadedFile {
  id: string;
  file: File;
  preview: string;
  progress: number;
  status: 'uploading' | 'uploaded' | 'error';
  url?: string;
  name: string;
  size: number;
  type: string;
  storage_path?: string;
}

interface ReceiptSecurityData {
  signature: string;
  barcodePayload: string;
  qrPayload: string;
  verificationUrl: string;
  receiptNumber: string;
  verificationToken: string;
}

// Zod Schema
const paymentSchema = z.object({
  student_id: z.string().min(1, 'Please select a student'),
  assignment_id: z.string().min(1, 'Please select a fee to pay'),
  amount_paid: z.number().min(1, 'Amount must be greater than 0'),
  payment_method: z.string().min(1, 'Please select a payment method'),
  payment_date: z.string().min(1, 'Payment date is required'),
  transaction_reference: z.string().optional(),
  notes: z.string().optional(),
});

type PaymentFormData = z.infer<typeof paymentSchema>;

// ============================================
// UTILITY FUNCTIONS - UPDATED WITH UUID FORMAT
// ============================================

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Generate UUID v4 for receipt numbers
const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Generate a shorter unique ID (8 characters)
const generateShortId = (): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

// NEW: Generate receipt number with UUID format (NO SEQUENTIAL - GUARANTEED UNIQUE)
const generateReceiptNumber = (branchCode: string = 'EBEO'): string => {
  const year = dayjs().format('YYYY');
  // Format: RCP/EBEO/2026/34yuxkil-yu
  const uuid = generateUUID();
  // Take first 8 chars of UUID for shorter format, or use full UUID
  const shortUuid = uuid.substring(0, 8);
  // Add a random component for extra uniqueness
  const randomComponent = generateShortId();
  return `RCP/${branchCode}/${year}/${shortUuid}-${randomComponent}`;
};

// Alternative: Generate receipt number with timestamp for guaranteed uniqueness
const generateReceiptNumberWithTimestamp = (branchCode: string = 'EBEO'): string => {
  const year = dayjs().format('YYYY');
  const timestamp = Date.now().toString(36).toUpperCase();
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `RCP/${branchCode}/${year}/${timestamp}-${random}`;
};

// Generate receipt number with retry mechanism (for extra safety)
const generateUniqueReceiptNumber = async (branchCode: string = 'EBEO', maxRetries: number = 3): Promise<string> => {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    const receiptNumber = generateReceiptNumber(branchCode);
    
    // Check if this receipt number already exists
    const { data, error } = await supabase
      .from('payments')
      .select('id')
      .eq('receipt_number', receiptNumber)
      .maybeSingle();
    
    if (error) {
      console.error('Error checking receipt number:', error);
      // If we can't check, still return the generated number
      // The database constraint will catch duplicates
      return receiptNumber;
    }
    
    if (!data) {
      // Receipt number is unique
      return receiptNumber;
    }
    
    console.log(`Receipt number ${receiptNumber} already exists, retrying... (Attempt ${attempt + 1})`);
  }
  
  // If all retries fail, use timestamp-based generation (guaranteed unique)
  const timestamp = Date.now().toString(36).toUpperCase();
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `RCP/${branchCode}/${dayjs().format('YYYY')}/${timestamp}-${random}`;
};

// Legacy function - kept for compatibility but no longer used for receipt numbers
const generateBranchReceiptCode = (branchCode: string, session: string, sequence: number): string => {
  const alphanumeric = generateAlphanumeric(6);
  return `${branchCode}/${session}/${alphanumeric}`;
};

const generateAlphanumeric = (length: number): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

const generateVerificationToken = (): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let token = 'EIS-VFY-';
  for (let i = 0; i < 12; i++) {
    token += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return token;
};

const generatePaymentId = async (): Promise<string> => {
  const { data, error } = await supabase.rpc('generate_payment_id');

  if (error) {
    console.error('Error generating payment ID:', error);
    throw new Error('Unable to generate a unique payment ID');
  }

  return data;
};

// Create receipt signature with improved security
const createReceiptSignature = async (paymentId: string): Promise<ReceiptSecurityData | null> => {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token;

    if (!accessToken) {
      console.error('No access token available for receipt signing');
      const verificationToken = generateVerificationToken();
      const signature = `EIS-SIG-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
      const barcodePayload = `EIS|${paymentId}|${signature}`;
      const qrPayload = JSON.stringify({
        v: 2,
        token: verificationToken,
        receipt: paymentId,
        signature: signature,
      });
      
      return {
        signature,
        barcodePayload,
        qrPayload,
        verificationUrl: `${supabaseUrl}/functions/v1/verify-receipt`,
        receiptNumber: paymentId,
        verificationToken,
      };
    }

    const response = await fetch(
      `${supabaseUrl}/functions/v1/create-receipt-signature`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ paymentId }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Receipt signing failed:', errorData);
      const verificationToken = generateVerificationToken();
      return {
        signature: `EIS-SIG-${Date.now()}`,
        barcodePayload: `EIS|${paymentId}|fallback`,
        qrPayload: JSON.stringify({ v: 1, receipt: paymentId }),
        verificationUrl: `${supabaseUrl}/functions/v1/verify-receipt`,
        receiptNumber: paymentId,
        verificationToken,
      };
    }

    const data = await response.json();
    
    if (!data.success) {
      console.error('Receipt signing error:', data.error);
      return null;
    }

    return {
      signature: data.signature,
      barcodePayload: data.barcodePayload,
      qrPayload: data.qrPayload,
      verificationUrl: data.verificationUrl,
      receiptNumber: data.receiptNumber,
      verificationToken: data.verificationToken || generateVerificationToken(),
    };
  } catch (error) {
    console.error('Error creating receipt signature:', error);
    const verificationToken = generateVerificationToken();
    return {
      signature: `EIS-SIG-${Date.now()}`,
      barcodePayload: `EIS|${paymentId}|fallback-${Date.now()}`,
      qrPayload: JSON.stringify({ v: 1, receipt: paymentId }),
      verificationUrl: `${import.meta.env.VITE_SUPABASE_URL || ''}/functions/v1/verify-receipt`,
      receiptNumber: paymentId,
      verificationToken,
    };
  }
};

// ============================================
// RECEIPT MODAL COMPONENT
// ============================================

const SuccessReceiptModal: React.FC<{
  isOpen: boolean;
  data: any | null;
  onClose: () => void;
  formatCurrencyFn: (amount: number) => string;
}> = ({ isOpen, data, onClose, formatCurrencyFn }) => {
  const [barcodeRef, setBarcodeRef] = useState<SVGSVGElement | null>(null);
  const receiptRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (barcodeRef && data) {
      try {
        const barcodeData = data.barcodePayload || `EIS|${data.receipt_number}|${data.signature || 'N/A'}`;
        JsBarcode(barcodeRef, barcodeData, {
          format: 'CODE128',
          width: 1.5,
          height: 60,
          displayValue: true,
          fontSize: 14,
          font: 'monospace',
          textMargin: 10,
          margin: 10,
          background: '#ffffff',
          lineColor: '#000000',
        });
      } catch (error) {
        console.error('Error generating barcode:', error);
      }
    }
  }, [barcodeRef, data]);

  if (!isOpen || !data) return null;

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    const receiptText = `
========================================
        EBENEZER INTERNATIONAL SCHOOL
              PAYMENT RECEIPT
========================================

Payment ID: ${data.payment_id || data.id || 'N/A'}
Receipt Number: ${data.receipt_number || 'N/A'}
Verification Token: ${data.verificationToken || 'N/A'}
Date: ${dayjs(data.payment_date || new Date()).format('MMMM D, YYYY h:mm A')}

Student: ${data.student_name || 'N/A'}
Student ID: ${data.student_id || 'N/A'}
Class: ${data.class_name || 'N/A'}

----------------------------------------
Fee: ${data.fee_name || 'N/A'}
Amount: ${formatCurrencyFn(data.amount || 0)}
Payment Method: ${data.payment_method || 'N/A'}
Reference: ${data.reference || data.transaction_reference || 'N/A'}

----------------------------------------
Security Status: AUTHENTIC
Branch Code: ${data.branch_code || 'EISO'}

========================================
This receipt is cryptographically signed.
Scan the QR code to verify authenticity.
Verify online: ${data.verificationUrl || ''}
Token: ${data.verificationToken || 'N/A'}

Thank you for your payment!
    `;

    const blob = new Blob([receiptText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `receipt_${data.receipt_number || 'payment'}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Receipt downloaded');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-3 sm:p-4">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-3 sm:p-4 flex items-center justify-between no-print">
          <h3 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Receipt className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
            Payment Receipt
          </h3>
          <button onClick={onClose} className="p-1.5 sm:p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all no-print">
            <X className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>

        <div ref={receiptRef} className="p-4 sm:p-6 space-y-4" id="receipt-content">
          {/* Receipt Header */}
          <div className="text-center border-b border-gray-200 dark:border-gray-700 pb-4">
            <h2 className="text-xl sm:text-2xl font-bold text-blue-700 dark:text-blue-400">Ebenezer International School</h2>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Official Payment Receipt</p>
            <p className="text-[10px] sm:text-xs text-gray-400 mt-1">Branch: {data.branch_code || 'EISO'}</p>
          </div>

          {/* Success Icon */}
          <div className="text-center">
            <div className="flex items-center justify-center mx-auto mb-3">
              <img src={transferSuccessImg} alt="Payment Success" className="w-16 h-16 sm:w-20 sm:h-20 object-contain" />
            </div>
            <h4 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
              Payment Successful!
            </h4>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
              Payment has been recorded and confirmed successfully
            </p>
          </div>

          {/* Security Badge */}
          <div className="flex items-center justify-center gap-2 p-2 rounded-lg border bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800">
            <ShieldCheck className="w-4 h-4 text-green-600 dark:text-green-400" />
            <span className="text-xs sm:text-sm font-medium text-green-700 dark:text-green-300">
              ✅ Cryptographically verified receipt
            </span>
            <span className="text-xs text-green-600 dark:text-green-400">🔒 Verified</span>
          </div>

          {/* Verification Token */}
          <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-3 text-center border border-gray-200 dark:border-gray-600">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Key className="w-3 h-3 text-gray-500" />
              <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400">Verification Token</p>
            </div>
            <p className="text-xs sm:text-sm font-mono font-bold text-blue-600 dark:text-blue-400 break-all">
              {data.verificationToken || generateVerificationToken()}
            </p>
            <p className="text-[8px] sm:text-[10px] text-gray-400 dark:text-gray-500 mt-1">
              Keep this token safe. It proves receipt authenticity.
            </p>
          </div>

          {/* Payment Details Grid */}
          <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm bg-gray-50 dark:bg-gray-700/30 rounded-lg p-3 sm:p-4">
            <div className="flex flex-col">
              <span className="text-gray-500 dark:text-gray-400">Payment ID</span>
              <span className="font-medium text-gray-900 dark:text-white font-mono text-[10px] sm:text-xs truncate">
                {data.payment_id || data.id || 'N/A'}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-gray-500 dark:text-gray-400">Receipt Number</span>
              <span className="font-medium text-gray-900 dark:text-white font-mono text-[10px] sm:text-xs truncate">
                {data.receipt_number || 'N/A'}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-gray-500 dark:text-gray-400">Date</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {dayjs(data.payment_date || new Date()).format('MMM D, YYYY h:mm A')}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-gray-500 dark:text-gray-400">Status</span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                Completed
              </span>
            </div>
            <div className="flex flex-col col-span-2">
              <span className="text-gray-500 dark:text-gray-400">Student</span>
              <span className="font-medium text-gray-900 dark:text-white">{data.student_name || 'N/A'}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-gray-500 dark:text-gray-400">Student ID</span>
              <span className="font-medium text-gray-900 dark:text-white">{data.student_id || 'N/A'}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-gray-500 dark:text-gray-400">Class</span>
              <span className="font-medium text-gray-900 dark:text-white">{data.class_name || 'N/A'}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-gray-500 dark:text-gray-400">Fee</span>
              <span className="font-medium text-gray-900 dark:text-white">{data.fee_name || 'N/A'}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-gray-500 dark:text-gray-400">Payment Method</span>
              <span className="font-medium text-gray-900 dark:text-white capitalize">{data.payment_method || 'N/A'}</span>
            </div>
            {data.transaction_reference && (
              <div className="flex flex-col col-span-2">
                <span className="text-gray-500 dark:text-gray-400">Transaction Ref</span>
                <span className="font-medium text-gray-900 dark:text-white font-mono text-[10px] sm:text-xs truncate">
                  {data.transaction_reference}
                </span>
              </div>
            )}
            <div className="flex flex-col col-span-2 pt-2 border-t border-gray-200 dark:border-gray-600">
              <span className="text-gray-500 dark:text-gray-400">Amount Paid</span>
              <span className="text-xl sm:text-2xl font-bold text-green-600 dark:text-green-400">
                {formatCurrencyFn(data.amount || 0)}
              </span>
            </div>
          </div>

          {/* Barcode Section */}
          <div className="barcode-section bg-gray-50 dark:bg-gray-700/30 rounded-lg p-4 text-center border border-gray-200 dark:border-gray-600">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Barcode className="w-4 h-4 text-gray-500" />
              <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400">Payment Authentication</p>
              <Lock className="w-4 h-4 text-gray-400" />
            </div>
            <svg ref={setBarcodeRef} className="mx-auto" />
            <p className="text-[8px] sm:text-[10px] text-gray-400 dark:text-gray-500 mt-1 font-mono break-all">
              {data.barcodePayload || `EIS|${data.receipt_number}|${data.signature || 'N/A'}`}
            </p>
            <p className="text-[8px] sm:text-[10px] text-gray-400 dark:text-gray-500 mt-1">
              Scan with any barcode scanner to verify authenticity
            </p>
          </div>

          {/* QR Code Section */}
          <div className="qr-section text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <QrCode className="w-4 h-4 text-gray-500" />
              <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400">Scan to Verify</p>
              <ShieldCheck className="w-4 h-4 text-green-500" />
            </div>
            <div className="inline-block bg-white dark:bg-gray-900 p-2 rounded-lg border border-gray-200 dark:border-gray-600">
              <QRCodeCanvas
                value={data.qrPayload || JSON.stringify({
                  v: 2,
                  token: data.verificationToken || 'N/A',
                  receipt: data.receipt_number,
                  signature: data.signature || 'N/A',
                })}
                size={150}
                bgColor="#ffffff"
                fgColor="#000000"
                level="H"
                includeMargin={true}
              />
            </div>
            <p className="text-[8px] sm:text-[10px] text-gray-400 dark:text-gray-500 mt-1">
              Scan with your phone to verify this receipt
            </p>
            {data.verificationUrl && (
              <p className="text-[8px] sm:text-[10px] text-gray-400 dark:text-gray-500 mt-1 break-all flex items-center justify-center gap-1">
                <ExternalLink className="w-3 h-3" />
                Verify at: {data.verificationUrl}
              </p>
            )}
          </div>

          <div className="text-center border-t border-gray-200 dark:border-gray-700 pt-3">
            <p className="text-[10px] sm:text-xs text-gray-400 dark:text-gray-500">
              Thank you for your payment. This receipt is cryptographically signed and can be verified.
            </p>
            <div className="flex items-center justify-center gap-4 mt-2">
              <span className="text-[8px] sm:text-[10px] text-gray-400 dark:text-gray-500">
                Receipt Code: {data.receipt_code || 'N/A'}
              </span>
              <span className="text-[8px] sm:text-[10px] text-gray-400 dark:text-gray-500">
                Payment ID: {data.payment_id || data.id || 'N/A'}
              </span>
            </div>
            <p className="text-[8px] sm:text-[10px] text-green-600 dark:text-green-400 mt-1 flex items-center justify-center gap-1">
              <Verified className="w-3 h-3" />
              Cryptographically verified receipt
            </p>
          </div>

          <div className="flex flex-col xs:flex-row gap-2 no-print">
            <button onClick={handlePrint} className="flex-1 px-4 py-2 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all text-sm flex items-center justify-center gap-2">
              <Printer className="w-4 h-4" />
              Print
            </button>
            <button onClick={handleDownload} className="flex-1 px-4 py-2 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all text-sm flex items-center justify-center gap-2">
              <Download className="w-4 h-4" />
              Download
            </button>
            <button onClick={onClose} className="flex-1 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg font-medium hover:opacity-90 transition-all text-sm">
              Done
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

interface RecordPaymentProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  redirectTo?: string;
}

const RecordPayment: React.FC<RecordPaymentProps> = ({ 
  onSuccess, 
  onCancel,
  redirectTo = '/payments'
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [students, setStudents] = useState<Student[]>([]);
  const [assignments, setAssignments] = useState<FeeAssignment[]>([]);
  const [searchStudent, setSearchStudent] = useState('');
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<FeeAssignment | null>(null);
  const [studentLoading, setStudentLoading] = useState(false);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [branchId, setBranchId] = useState<string>('');
  const [branchCode, setBranchCode] = useState<string>('EBEO');
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<PaymentFormData>({
    resolver: zodResolver(paymentSchema),
    defaultValues: {
      payment_date: dayjs().format('YYYY-MM-DD'),
      payment_method: 'cash',
    },
  });

  const watchedStudentId = watch('student_id');
  const watchedAssignmentId = watch('assignment_id');
  const watchedAmount = watch('amount_paid');

  // Load user branch
  useEffect(() => {
    const getBranch = async () => {
      if (user?.id) {
        const { data, error } = await supabase
          .from('users')
          .select('branch_id')
          .eq('id', user.id)
          .single();

        if (!error && data) {
          setBranchId(data.branch_id);
          // Get branch code
          const { data: branchData } = await supabase
            .from('branches')
            .select('branch_code, school_name')
            .eq('id', data.branch_id)
            .single();
          if (branchData?.branch_code) {
            setBranchCode(branchData.branch_code);
          }
          loadStudents(data.branch_id);
        }
      }
    };
    getBranch();
  }, [user]);

  // Load students
  const loadStudents = async (branchId: string) => {
    setStudentLoading(true);
    try {
      const { data, error } = await supabase
        .from('students')
        .select(`
          id,
          first_name,
          last_name,
          admission_number,
          branch_id,
          email,
          class:class_id (name)
        `)
        .eq('branch_id', branchId)
        .eq('current_status', 'active')
        .order('first_name');

      if (error) throw error;

      const formattedStudents = data?.map((item: any) => ({
        ...item,
        class_name: item.class?.name || 'N/A',
      })) || [];

      setStudents(formattedStudents);
    } catch (error: any) {
      console.error('Error loading students:', error);
      toast.error(error.message || 'Failed to load students');
    } finally {
      setStudentLoading(false);
    }
  };

  // Load student's fee assignments - ONLY UNPAID OR PARTIALLY PAID (INSTALLMENTS)
  const loadStudentAssignments = async (studentId: string) => {
    setAssignmentsLoading(true);
    setAssignments([]);
    setSelectedAssignment(null);
    setValue('assignment_id', '');
    
    try {
      const { data, error } = await supabase
        .from('student_fee_assignments')
        .select(`
          id,
          fee_id,
          original_amount,
          discount_amount,
          amount_due,
          amount_paid,
          balance,
          payment_status,
          due_date,
          is_active,
          term,
          session,
          academic_session_id,
          student_id,
          fees!inner (
            id,
            name,
            category,
            description
          )
        `)
        .eq('student_id', studentId)
        .eq('is_active', true)
        .neq('payment_status', 'paid')  // This allows both 'unpaid' and 'partial' (installments)
        .order('due_date', { ascending: true });

      if (error) throw error;

      const formattedAssignments = (data || []).map((item: any) => {
        const sessionName = item.session || 'N/A';
        const termName = item.term || 'N/A';
        
        return {
          id: item.id,
          fee_id: item.fee_id,
          fee_name: item.fees?.name || 'Unknown Fee',
          fee_category: item.fees?.category || 'N/A',
          fee_description: item.fees?.description || '',
          original_amount: item.original_amount,
          discount_amount: item.discount_amount || 0,
          amount_due: item.amount_due,
          amount_paid: item.amount_paid || 0,
          balance: item.balance,
          payment_status: item.payment_status,
          due_date: item.due_date,
          is_active: item.is_active,
          session_id: item.academic_session_id,
          term_id: null,
          session_name: sessionName,
          term_name: termName,
          academic_session: sessionName,
          academic_term: termName,
          student_id: item.student_id,
        };
      });

      setAssignments(formattedAssignments);
      
      if (formattedAssignments.length === 0) {
        toast('No outstanding fee assignments found for this student. All fees are paid!');
      }
    } catch (error: any) {
      console.error('Error loading assignments:', error);
      toast.error(error.message || 'Failed to load fee assignments');
    } finally {
      setAssignmentsLoading(false);
    }
  };

  // Handle student selection
  const handleStudentSelect = (student: Student) => {
    setSelectedStudent(student);
    setValue('student_id', student.id);
    setShowStudentDropdown(false);
    setSearchStudent(`${student.first_name} ${student.last_name} (${student.admission_number})`);
    loadStudentAssignments(student.id);
  };

  // Handle assignment selection
  const handleAssignmentSelect = (assignmentId: string) => {
    const assignment = assignments.find(a => a.id === assignmentId);
    if (assignment) {
      setSelectedAssignment(assignment);
      setValue('assignment_id', assignmentId);
      setValue('amount_paid', assignment.balance);
    }
  };

  // Filter students based on search
  const filteredStudents = students.filter(student =>
    `${student.first_name} ${student.last_name} ${student.admission_number}`
      .toLowerCase()
      .includes(searchStudent.toLowerCase())
  );

  // Handle file upload
  const handleFileUpload = (files: FileList | null) => {
    if (!files) return;

    const newFiles: UploadedFile[] = Array.from(files).map((file) => ({
      id: `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      preview: URL.createObjectURL(file),
      progress: 0,
      status: 'uploading',
      name: file.name,
      size: file.size,
      type: file.type,
    }));

    setUploadedFiles((prev) => [...prev, ...newFiles]);

    newFiles.forEach((fileData) => {
      uploadFileToStorage(fileData);
    });
  };

  const uploadFileToStorage = async (fileData: UploadedFile) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('You must be logged in to upload files');
        setUploadedFiles((prev) =>
          prev.map((f) =>
            f.id === fileData.id ? { ...f, status: 'error' } : f
          )
        );
        return;
      }

      const timestamp = Date.now();
      const sanitizedName = fileData.file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const filePath = `payments/${branchId}/${fileData.id}/${timestamp}_${sanitizedName}`;

      const { data, error } = await supabase.storage
        .from('payment-proofs')
        .upload(filePath, fileData.file, {
          cacheControl: '3600',
          upsert: false,
          contentType: fileData.file.type,
        });

      if (error) {
        console.error('Upload error:', error);
        toast.error(`Upload failed: ${error.message}`);
        setUploadedFiles((prev) =>
          prev.map((f) =>
            f.id === fileData.id ? { ...f, status: 'error' } : f
          )
        );
        return;
      }

      const { data: urlData } = supabase.storage
        .from('payment-proofs')
        .getPublicUrl(filePath);

      setUploadedFiles((prev) =>
        prev.map((f) =>
          f.id === fileData.id
            ? { 
                ...f, 
                status: 'uploaded', 
                url: urlData.publicUrl,
                storage_path: filePath,
                progress: 100 
              }
            : f
        )
      );

      toast.success(`File uploaded: ${fileData.file.name}`);
    } catch (error: any) {
      console.error('Error in uploadFileToStorage:', error);
      setUploadedFiles((prev) =>
        prev.map((f) =>
          f.id === fileData.id ? { ...f, status: 'error' } : f
        )
      );
      toast.error(`Failed to upload: ${fileData.file.name} - ${error.message}`);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileUpload(e.dataTransfer.files);
  };

  const removeFile = (fileId: string) => {
    const file = uploadedFiles.find(f => f.id === fileId);
    if (file?.preview) {
      URL.revokeObjectURL(file.preview);
    }
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return Image;
    return File;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Reset form
  const resetForm = () => {
    reset();
    setSelectedStudent(null);
    setSelectedAssignment(null);
    setSearchStudent('');
    setAssignments([]);
    uploadedFiles.forEach(f => URL.revokeObjectURL(f.preview));
    setUploadedFiles([]);
  };

  // Create notification for student/parent
  const createNotification = async (payment: any, student: any) => {
    try {
      // Find the parent of the student
      const { data: parentData } = await supabase
        .from('parents')
        .select('user_id')
        .eq('id', student.parent_id)
        .single();

      if (parentData) {
        // Create notification for parent
        await supabase
          .from('notifications')
          .insert({
            user_id: parentData.user_id,
            title: 'Payment Recorded',
            message: `A payment of ${formatCurrency(payment.amount_paid)} has been recorded for ${student.first_name} ${student.last_name}`,
            type: 'payment',
            is_read: false,
            created_at: new Date().toISOString(),
            data: {
              path: `/parent/payment/${student.id}`,
              payment_id: payment.id,
              student_id: student.id,
            }
          });
      }

      // If student has a user account
      if (student.user_id) {
        await supabase
          .from('notifications')
          .insert({
            user_id: student.user_id,
            title: 'Payment Recorded',
            message: `A payment of ${formatCurrency(payment.amount_paid)} has been recorded for your fees.`,
            type: 'payment',
            is_read: false,
            created_at: new Date().toISOString(),
            data: {
              path: `/student/payments`,
              payment_id: payment.id,
            }
          });
      }
    } catch (error) {
      console.error('Error creating notification:', error);
    }
  };

  // ============================================
  // UPDATED SUBMIT HANDLER - WITH UUID RECEIPT NUMBERS
  // ============================================
  const onSubmit = async (data: PaymentFormData) => {
    if (!branchId) {
      toast.error('No branch assigned. Please contact administrator.');
      return;
    }

    const uploadingFiles = uploadedFiles.filter(f => f.status === 'uploading');
    if (uploadingFiles.length > 0) {
      toast.error('Please wait for files to finish uploading');
      return;
    }

    if (selectedAssignment && data.amount_paid > selectedAssignment.balance) {
      toast.error(`Amount cannot exceed remaining balance of ${formatCurrency(selectedAssignment.balance)}`);
      return;
    }

    setSubmitting(true);
    try {
      // Get student details
      const student = students.find(s => s.id === data.student_id);
      if (!student) {
        toast.error('Student not found');
        setSubmitting(false);
        return;
      }

      // Generate IDs
      const paymentId = await generatePaymentId();
      
      // ==========================================
      // USE UUID-BASED RECEIPT NUMBER (NO DUPLICATES)
      // ==========================================
      const receiptNumber = generateReceiptNumber(branchCode);
      
      // Generate receipt code (separate from receipt number)
      const receiptCode = `EBE/${dayjs().format('YYYY')}/${generateShortId()}`;
      
      const verificationToken = generateVerificationToken();

      const uploadedFilesData = uploadedFiles
        .filter(f => f.status === 'uploaded' && f.url)
        .map(f => ({
          name: f.name,
          size: f.size,
          type: f.type,
          url: f.url,
          storage_path: f.storage_path,
        }));

      const newBalance = (selectedAssignment?.balance || 0) - data.amount_paid;
      const isFullyPaid = newBalance <= 0;

      const paymentData = {
        payment_id: paymentId,
        receipt_number: receiptNumber,  // UUID format: RCP/EBEO/2026/34yuxkil-yu
        receipt_code: receiptCode,
        verification_token: verificationToken,
        student_id: data.student_id,
        fee_id: selectedAssignment?.fee_id || null,
        assignment_id: data.assignment_id,
        amount: selectedAssignment?.amount_due || data.amount_paid,
        amount_paid: data.amount_paid,
        balance: Math.max(newBalance, 0),
        payment_method: data.payment_method,
        payment_date: data.payment_date,
        due_date: selectedAssignment?.due_date || null,
        // If fully paid, mark as completed; otherwise keep as pending (allows installments)
        status: isFullyPaid ? 'completed' : 'pending',
        transaction_reference: data.transaction_reference || paymentId,
        payment_proof_url: uploadedFilesData.length > 0 ? uploadedFilesData.map(f => f.url).join(',') : null,
        receipt_url: uploadedFilesData.length > 0 ? uploadedFilesData[0].url : null,
        branch_id: branchId,
        branch_code: branchCode,
        created_by: user?.id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        receipt_security_status: 'PENDING',
        receipt_security_version: 2,
        metadata: {
          notes: data.notes || null,
          created_by: user?.email || 'System',
          files: uploadedFilesData,
          session: selectedAssignment?.session_name || null,
          term: selectedAssignment?.term_name || null,
          session_id: selectedAssignment?.session_id || null,
          term_id: selectedAssignment?.term_id || null,
          student_name: `${student.first_name} ${student.last_name}`,
          student_id: student.admission_number,
          fee_name: selectedAssignment?.fee_name || null,
          verification_token: verificationToken,
          receipt_code: receiptCode,
          // Track if this is an installment payment
          is_installment: !isFullyPaid,
          remaining_balance: Math.max(newBalance, 0),
        },
      };

      console.log('Inserting payment data with UUID receipt:', paymentData);

      const { data: insertedData, error } = await supabase
        .from('payments')
        .insert([paymentData])
        .select()
        .single();

      if (error) {
        console.error('Insert error:', error);
        throw error;
      }

      // Update assignment (supports installments)
      const { error: updateError } = await supabase
        .from('student_fee_assignments')
        .update({
          amount_paid: (selectedAssignment?.amount_paid || 0) + data.amount_paid,
          balance: Math.max(newBalance, 0),
          payment_status: isFullyPaid ? 'paid' : 'partial',  // 'partial' allows installments
          updated_at: new Date().toISOString(),
        })
        .eq('id', data.assignment_id);

      if (updateError) {
        console.error('Error updating assignment:', updateError);
        toast.warning('Payment recorded but fee assignment update failed. Please check the balance manually.');
      }

      // Generate receipt signature
      let securityData = null;
      if (insertedData?.id) {
        securityData = await createReceiptSignature(insertedData.id);
        if (securityData) {
          await supabase
            .from('payments')
            .update({
              receipt_signature: securityData.signature,
              receipt_barcode_payload: securityData.barcodePayload,
              receipt_qr_payload: securityData.qrPayload,
              receipt_security_status: 'AUTHENTIC',
              verification_token: securityData.verificationToken || verificationToken,
            })
            .eq('id', insertedData.id);
        }
      }

      // Create notification
      await createNotification(insertedData, student);

      // Prepare receipt data
      const receiptData = {
        id: insertedData?.id,
        payment_id: paymentId,
        receipt_number: receiptNumber,
        receipt_code: receiptCode,
        amount: data.amount_paid,
        payment_date: data.payment_date,
        payment_method: data.payment_method,
        student_name: `${student.first_name} ${student.last_name}`,
        student_id: student.admission_number,
        class_name: student.class_name || 'N/A',
        fee_name: selectedAssignment?.fee_name || 'N/A',
        transaction_reference: data.transaction_reference || paymentId,
        branch_code: branchCode,
        signature: securityData?.signature,
        barcodePayload: securityData?.barcodePayload,
        qrPayload: securityData?.qrPayload,
        verificationToken: securityData?.verificationToken || verificationToken,
        verificationUrl: `${import.meta.env.VITE_SUPABASE_URL || ''}/functions/v1/verify-receipt`,
        // Include installment info
        is_installment: !isFullyPaid,
        remaining_balance: Math.max(newBalance, 0),
        installment_message: !isFullyPaid ? `Remaining balance: ${formatCurrency(Math.max(newBalance, 0))}` : 'Fully paid',
      };

      setReceiptData(receiptData);
      setShowReceipt(true);
      
      const installmentMessage = !isFullyPaid ? ` (Installment payment. Remaining: ${formatCurrency(Math.max(newBalance, 0))})` : ' (Fully paid)';
      toast.success(`Payment recorded successfully! Receipt: ${receiptNumber}${installmentMessage}`);
      
      uploadedFiles.forEach(f => URL.revokeObjectURL(f.preview));
      resetForm();

      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error('Error recording payment:', error);
      toast.error(error.message || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  const paymentMethods = [
    { value: 'cash', label: 'Cash', icon: Banknote },
    { value: 'bank_transfer', label: 'Bank Transfer', icon: Building },
    { value: 'card', label: 'Card', icon: CreditCard },
    { value: 'pos', label: 'POS', icon: Smartphone },
    { value: 'wallet', label: 'Wallet', icon: Wallet },
  ];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'partial':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'unpaid':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          {(onCancel || redirectTo !== '/payments') && (
            <button
              onClick={() => {
                if (onCancel) {
                  onCancel();
                } else {
                  navigate(redirectTo);
                }
              }}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Record Payment</h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              Record a payment against a student's outstanding fee
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={resetForm}
            className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-all"
          >
            <X className="w-4 h-4" />
            Reset
          </button>
        </div>
      </div>

      {/* Payment Form */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="p-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Student Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Student *
              </label>
              <div className="relative">
                <div className="flex items-center border border-gray-200 dark:border-gray-700 rounded-xl focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent transition-all bg-white dark:bg-gray-900">
                  <User className="w-5 h-5 ml-3 text-gray-400" />
                  <input
                    type="text"
                    value={searchStudent}
                    onChange={(e) => {
                      setSearchStudent(e.target.value);
                      setShowStudentDropdown(true);
                      if (!e.target.value) {
                        setSelectedStudent(null);
                        setValue('student_id', '');
                        setAssignments([]);
                      }
                    }}
                    onFocus={() => setShowStudentDropdown(true)}
                    placeholder="Search student by name or admission number..."
                    className="w-full px-3 py-2.5 bg-transparent focus:outline-none dark:text-white"
                    disabled={studentLoading}
                  />
                  {studentLoading && (
                    <Loader2 className="w-5 h-5 mr-3 text-gray-400 animate-spin" />
                  )}
                  {selectedStudent && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedStudent(null);
                        setSearchStudent('');
                        setValue('student_id', '');
                        setAssignments([]);
                      }}
                      className="mr-2 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                    >
                      <X className="w-4 h-4 text-gray-400" />
                    </button>
                  )}
                </div>

                {/* Student Dropdown */}
                {showStudentDropdown && searchStudent && !studentLoading && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                    {filteredStudents.length === 0 ? (
                      <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                        No students found
                      </div>
                    ) : (
                      filteredStudents.map((student) => (
                        <button
                          key={student.id}
                          type="button"
                          onClick={() => handleStudentSelect(student)}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-all flex items-center justify-between"
                        >
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {student.first_name} {student.last_name}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {student.admission_number} • {student.class_name}
                            </p>
                          </div>
                          {watchedStudentId === student.id && (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
              {errors.student_id && (
                <p className="mt-1 text-sm text-red-500">{errors.student_id.message}</p>
              )}
            </div>

            {/* Fee Assignment Selection */}
            {selectedStudent && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Select Fee to Pay *
                </label>
                {assignmentsLoading ? (
                  <div className="flex items-center justify-center p-8">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                    <span className="ml-3 text-gray-500">Loading assignments...</span>
                  </div>
                ) : assignments.length === 0 ? (
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl text-center">
                    <CheckCircle className="w-8 h-8 mx-auto text-green-500 mb-2" />
                    <p className="text-green-700 dark:text-green-300">
                      No outstanding fees! 🎉
                    </p>
                    <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                      This student has no unpaid or partially paid fees.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <select
                      {...register('assignment_id')}
                      onChange={(e) => handleAssignmentSelect(e.target.value)}
                      className={`w-full px-4 py-2.5 rounded-xl border ${
                        errors.assignment_id ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'
                      } bg-white dark:bg-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all dark:text-white`}
                    >
                      <option value="">Select a fee to pay</option>
                      {assignments.map((assignment) => (
                        <option key={assignment.id} value={assignment.id}>
                          {assignment.fee_name} - {formatCurrency(assignment.balance)} remaining 
                          ({assignment.payment_status === 'partial' ? 'Installment' : assignment.payment_status}) - {assignment.session_name} • {assignment.term_name}
                        </option>
                      ))}
                    </select>

                    {/* Selected Assignment Details */}
                    {selectedAssignment && (
                      <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500 dark:text-gray-400">Fee</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {selectedAssignment.fee_name}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500 dark:text-gray-400">Category</span>
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {selectedAssignment.fee_category}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 border-t border-gray-200 dark:border-gray-600 pt-2">
                          <div>
                            <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              Session
                            </span>
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              {selectedAssignment.session_name || 'N/A'}
                            </p>
                          </div>
                          <div>
                            <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Term
                            </span>
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              {selectedAssignment.term_name || 'N/A'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500 dark:text-gray-400">Original Amount</span>
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {formatCurrency(selectedAssignment.original_amount)}
                          </span>
                        </div>
                        {selectedAssignment.discount_amount > 0 && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-500 dark:text-gray-400">Discount</span>
                            <span className="text-sm text-green-600">
                              -{formatCurrency(selectedAssignment.discount_amount)}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500 dark:text-gray-400">Amount Paid</span>
                          <span className="text-sm text-green-600">
                            {formatCurrency(selectedAssignment.amount_paid)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-600 pt-2">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Remaining Balance</span>
                          <span className="text-lg font-bold text-blue-600">
                            {formatCurrency(selectedAssignment.balance)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500 dark:text-gray-400">Status</span>
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(selectedAssignment.payment_status)}`}>
                            {selectedAssignment.payment_status === 'partial' ? 'Installment' : selectedAssignment.payment_status.charAt(0).toUpperCase() + selectedAssignment.payment_status.slice(1)}
                          </span>
                        </div>
                        {selectedAssignment.due_date && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-500 dark:text-gray-400">Due Date</span>
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {dayjs(selectedAssignment.due_date).format('MMM D, YYYY')}
                            </span>
                          </div>
                        )}
                        {selectedAssignment.fee_description && (
                          <div className="mt-2 p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
                            <p className="text-xs text-gray-500 dark:text-gray-400">{selectedAssignment.fee_description}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {errors.assignment_id && (
                  <p className="mt-1 text-sm text-red-500">{errors.assignment_id.message}</p>
                )}
              </div>
            )}

            {/* Amount */}
            {selectedAssignment && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Amount to Pay *
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="number"
                      step="0.01"
                      {...register('amount_paid', { valueAsNumber: true })}
                      className={`w-full pl-10 pr-4 py-2.5 rounded-xl border ${
                        errors.amount_paid ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'
                      } bg-white dark:bg-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all dark:text-white`}
                      placeholder="0.00"
                      max={selectedAssignment.balance}
                    />
                  </div>
                  {errors.amount_paid && (
                    <p className="mt-1 text-sm text-red-500">{errors.amount_paid.message}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Maximum: {formatCurrency(selectedAssignment.balance)}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    New Balance After Payment
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={watchedAmount ? 
                        formatCurrency(Math.max(selectedAssignment.balance - watchedAmount, 0)) : 
                        formatCurrency(selectedAssignment.balance)
                      }
                      disabled
                      className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 cursor-not-allowed dark:text-white"
                    />
                  </div>
                  {watchedAmount && (
                    <p className={`mt-1 text-sm ${
                      selectedAssignment.balance - watchedAmount === 0 ? 'text-green-600' :
                      selectedAssignment.balance - watchedAmount < 0 ? 'text-red-600' :
                      'text-yellow-600'
                    }`}>
                      {selectedAssignment.balance - watchedAmount === 0 ? '✅ Fee will be fully paid' :
                       selectedAssignment.balance - watchedAmount < 0 ? '⚠️ Overpayment' :
                       `📋 Installment payment. ${formatCurrency(selectedAssignment.balance - watchedAmount)} remaining`}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Payment Method & Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Payment Method *
                </label>
                <select
                  {...register('payment_method')}
                  className={`w-full px-4 py-2.5 rounded-xl border ${
                    errors.payment_method ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'
                  } bg-white dark:bg-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all dark:text-white`}
                >
                  {paymentMethods.map((method) => (
                    <option key={method.value} value={method.value}>
                      {method.label}
                    </option>
                  ))}
                </select>
                {errors.payment_method && (
                  <p className="mt-1 text-sm text-red-500">{errors.payment_method.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Payment Date *
                </label>
                <input
                  type="date"
                  {...register('payment_date')}
                  className={`w-full px-4 py-2.5 rounded-xl border ${
                    errors.payment_date ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'
                  } bg-white dark:bg-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all dark:text-white`}
                />
                {errors.payment_date && (
                  <p className="mt-1 text-sm text-red-500">{errors.payment_date.message}</p>
                )}
              </div>
            </div>

            {/* Transaction Reference */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Transaction Reference
              </label>
              <input
                type="text"
                {...register('transaction_reference')}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all dark:text-white"
                placeholder="Enter transaction reference"
              />
            </div>

            {/* File Upload Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Payment Proof / Documents
              </label>
              
              <div
                className={`border-2 border-dashed rounded-xl p-6 text-center transition-all duration-300 ${
                  isDragging
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 scale-[1.02]'
                    : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                  multiple
                  onChange={(e) => handleFileUpload(e.target.files)}
                />
                <Upload className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-500 mb-3" />
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Drag & drop files here or click to browse
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Supports: Images, PDF, Word, Excel (Max 10MB each)
                </p>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-3 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg text-sm font-medium hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-all"
                >
                  Browse Files
                </button>
              </div>

              {/* Uploaded Files List */}
              {uploadedFiles.length > 0 && (
                <div className="mt-4 space-y-2">
                  {uploadedFiles.map((file) => {
                    const FileIcon = getFileIcon(file.type);
                    const isUploading = file.status === 'uploading';
                    const isError = file.status === 'error';
                    const isUploaded = file.status === 'uploaded';

                    return (
                      <div
                        key={file.id}
                        className={`flex items-center gap-3 p-3 rounded-xl border ${
                          isError
                            ? 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
                            : isUploaded
                            ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
                            : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50'
                        }`}
                      >
                        <div className={`p-2 rounded-lg ${
                          isError
                            ? 'bg-red-100 dark:bg-red-900/30'
                            : isUploaded
                            ? 'bg-green-100 dark:bg-green-900/30'
                            : 'bg-gray-100 dark:bg-gray-700'
                        }`}>
                          {file.type.startsWith('image/') ? (
                            <img
                              src={file.preview}
                              alt={file.name}
                              className="w-10 h-10 object-cover rounded"
                            />
                          ) : (
                            <FileIcon className={`w-6 h-6 ${
                              isError
                                ? 'text-red-500'
                                : isUploaded
                                ? 'text-green-500'
                                : 'text-gray-500'
                            }`} />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {file.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {formatFileSize(file.size)}
                            {isUploading && ' • Uploading...'}
                            {isError && ' • Upload failed'}
                            {isUploaded && ' • Uploaded ✓'}
                          </p>
                          {isUploading && (
                            <div className="w-full h-1 bg-gray-200 dark:bg-gray-700 rounded-full mt-1 overflow-hidden">
                              <div
                                className="h-full bg-blue-500 transition-all duration-300"
                                style={{ width: `${file.progress}%` }}
                              />
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {isUploaded && file.url && (
                            <a
                              href={file.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-all"
                              title="View file"
                            >
                              <Eye className="w-4 h-4 text-gray-500" />
                            </a>
                          )}
                          <button
                            type="button"
                            onClick={() => removeFile(file.id)}
                            className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-all text-red-500"
                            disabled={isUploading}
                            title="Remove file"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Notes
              </label>
              <textarea
                {...register('notes')}
                rows={3}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all dark:text-white"
                placeholder="Additional notes about this payment..."
              />
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800 dark:text-blue-300">
                <p>• Shows outstanding fees (unpaid or partially paid - installment support).</p>
                <p>• Each fee assignment is linked to a session and term.</p>
                <p>• Payment status will update to "paid" when balance is cleared.</p>
                <p>• Partial payments create installments (payment status: "partial").</p>
                <p>• Upload payment proof or receipt for verification.</p>
                <p>• All payments are recorded with cryptographic security.</p>
                <p>• Receipts include QR codes and verification tokens.</p>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex flex-col sm:flex-row items-center justify-end gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => {
                  if (onCancel) {
                    onCancel();
                  } else {
                    navigate(redirectTo);
                  }
                }}
                className="w-full sm:w-auto px-6 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !selectedStudent || !selectedAssignment}
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-medium hover:opacity-90 transition-all shadow-lg shadow-blue-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Recording...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Record Payment
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Success Receipt Modal */}
      <SuccessReceiptModal
        isOpen={showReceipt}
        data={receiptData}
        onClose={() => {
          setShowReceipt(false);
          setReceiptData(null);
          if (onSuccess) {
            onSuccess();
          } else {
            navigate(redirectTo);
          }
        }}
        formatCurrencyFn={formatCurrency}
      />
    </div>
  );
};

export default RecordPayment;